import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { z } from '@customerio/hydra-validation';
import { useSnackbars } from '@customerio/pluma-components/react';
import {
  Authenticators,
  isGoogleOneClickMustChooseUserAccountError,
  isTwoFactorAuthLinkRequiredError,
  isTwoFactorAuthRequiredError,
  isLoginError,
  isUnknownLoginError,
  LoginError,
  LoginErrorType,
  type ChooseUserEntryDataObject,
} from '@customerio/hydra-authentication';
import { changeToSession, sessionsStore, updateSessions } from '@customerio/hydra-session-state';
import { clearAttemptedTransitionUrl, getAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { session } from '@/services/session';
import {
  AddSessionResponse,
  authenticateWithSso,
  fetchSsoInfo,
  openGoogleOIDCPopup,
  type LoginMeta,
  type SsoConfiguration,
} from '@/services/oauth';
import { isEmptyString } from '@/utils/string';
import { workspaceUrlFromCurrentPath } from '@/utils/navigation';
import { sentryCaptureException } from '@/utils/sentry';
import { getGaClientId } from '@/utils/ga-client-id';
import { decodeJwtPayload } from './utils/jwt';
import { hardRedirect, toSafeInternalPath } from '@/utils/hard-redirect';
import { LoginMethod, setLastLoginMethod } from './utils/last-login-method';
import {
  getTwoFactorAuthLinkCookie,
  removeTwoFactorAuthLinkCookie,
  setTwoFactorAuthLinkCookie,
} from '@/utils/two-factor-link-cookie';
import { isOtherThanGoogleSso, type FormattedChooseUserEntry } from './utils/choose-user';
import { isUnrecognizedLoginError, loginErrorMessage } from '@/services/auth/login-error-message';
import { LoginAnalyticsEvent, trackLoginEvent } from './utils/track-login-event';
import { resolveEffectiveStep } from './utils/effective-step';

export type LoginStep = 'email' | 'password' | 'two_factor_authentication' | '2fa_email_link' | 'sso' | 'choose_user';

const emailSchema = z.string().trim().email();

type LoginFlowValue = {
  step: LoginStep;
  email: string;
  password: string;
  twoFactorAuthCode: string;
  twoFactorAuthLoginToken: string;
  emailError: string | undefined;
  isSubmitting: boolean;
  isAddingAccount: boolean;
  ssoConfiguration: SsoConfiguration | null;
  userExternalIdentityEmail: string | null;
  chooseUserEntries: ChooseUserEntryDataObject[];
  googleAccountEmail: string;
  /** Whether the email step has been submitted this session (gates password/2FA). */
  hasSubmittedEmail: boolean;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setTwoFactorAuthCode: (value: string) => void;
  submitEmail: () => Promise<void>;
  submitPassword: () => Promise<void>;
  submitTwoFactor: () => Promise<void>;
  /** Resolves `true` on success; `false` if the link was invalid/expired. */
  validateTwoFactorLink: (token: string) => Promise<boolean>;
  submitSso: () => Promise<void>;
  googleOneClick: (email?: string) => Promise<void>;
  chooseUser: (entry: FormattedChooseUserEntry) => Promise<void>;
  resetToEmailStep: () => void;
};

const LoginFlowContext = createContext<LoginFlowValue | null>(null);

export function useLoginFlow(): LoginFlowValue {
  const value = use(LoginFlowContext);
  if (value == null) {
    throw new Error('useLoginFlow must be used within a LoginFlowProvider');
  }
  return value;
}

// ---------------------------------------------------------------------------
// Module-level helpers (kept out of the hooks so each function stays simple).
// ---------------------------------------------------------------------------

function resolveRedirectTarget(origin: string | undefined): string {
  const attempted = getAttemptedTransitionUrl();
  clearAttemptedTransitionUrl();
  // The URL the user was originally trying to reach wins over `?origin=`,
  // matching Ember's `handleAuthentication` (attempted transition first, then
  // the stored origin). Both are attacker-influenceable, so only honor
  // same-origin in-app paths — the post-login `hardRedirect` would otherwise be
  // an open-redirect sink.
  return toSafeInternalPath(attempted) ?? toSafeInternalPath(origin) ?? '/';
}

/** Multi-account: if the login response carries session metadata, switch to it. */
function applyAddSessionResponse(response: unknown): void {
  const parsed = AddSessionResponse.safeParse(response);
  if (parsed.success) {
    changeToSession(parsed.data.session_index);
    updateSessions(parsed.data.sessions);
  }
}

/** Drop any query string / hash so the path can be re-based onto another workspace. */
function stripQueryAndHash(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

/** A workspace-scoped path that pins a concrete (account-specific) workspace id. */
const ACCOUNT_PINNED_WORKSPACE_PATH = /^\/workspaces\/\d+(?:\/|$)/;

/**
 * Resolve where to send the user after a successful login.
 *
 * The base target is the attempted-transition URL or the `?origin=` deep link
 * (Ember parity — return the user to the page they were on). But both are
 * captured against whatever account the user was last on, so after logging into
 * a *different* account they pin a workspace id that isn't ours — and Hydra
 * resolves the account from the workspace id in the URL, so honoring it lands the
 * user on (or bounces them off) the other account (FPS-2523).
 *
 * We can't tell which account we just authenticated as without a round-trip — a
 * normal (non-add-session) login returns an empty body, so there's no session
 * metadata to check. Instead of validating the id, we neutralize it: rewrite an
 * account-pinned `/workspaces/:id/...` target onto the `last` sentinel (dropping
 * trailing resource-id segments), exactly like switching workspaces. `last`
 * resolves against whatever account the landing loads — and because it isn't a
 * numeric id, the landing resolves the *current* account rather than the URL's —
 * so the view is preserved without pinning a foreign account. Targets that don't
 * pin an account (already `last`, or non-workspace like `/settings/...`) are
 * account-agnostic and pass through untouched.
 */
function resolvePostLoginTarget(origin: string | undefined): string {
  const target = resolveRedirectTarget(origin);
  const path = stripQueryAndHash(target);
  if (!ACCOUNT_PINNED_WORKSPACE_PATH.test(path)) {
    return target;
  }
  return workspaceUrlFromCurrentPath('last', path);
}

type LoginErrorActions = {
  goToStep: (step: LoginStep) => void;
  goToChooser: (entries: ChooseUserEntryDataObject[], jwtToken: string, googleAccountEmail: string) => void;
  setTwoFactorAuthLoginToken: (token: string) => void;
  clearTwoFactorAuthCode: () => void;
  showError: (message: string) => void;
};

/** Route an authentication error to the next step or surface it. */
function applyLoginError(err: unknown, actions: LoginErrorActions, ssoProvider?: string): void {
  if (isGoogleOneClickMustChooseUserAccountError(err)) {
    actions.goToChooser(err.usersData, err.chooseUserJWTToken, err.googleAccountEmail);
    return;
  }
  if (isTwoFactorAuthRequiredError(err)) {
    actions.setTwoFactorAuthLoginToken(err.twoFactorAuthLoginToken);
    actions.goToStep('two_factor_authentication');
    return;
  }
  if (isTwoFactorAuthLinkRequiredError(err)) {
    setTwoFactorAuthLinkCookie(err.twoFactorAuthLinkToken);
    actions.goToStep('2fa_email_link');
    return;
  }
  if (
    isLoginError(err) &&
    (err.type === LoginErrorType.TWO_FACTOR_INVALID_CODE || err.type === LoginErrorType.TWO_FACTOR_USED_CODE)
  ) {
    actions.clearTwoFactorAuthCode();
  }
  if (isUnrecognizedLoginError(err)) {
    // Mirror Ember's login controller, which captured errors it couldn't map to
    // a known login scenario — they otherwise surface only the generic snackbar
    // with no trace to debug from. Scoped to genuinely-unrecognized failures so
    // expected conditions (401/429/attempts-limit, all typed as UnknownLoginError
    // but with their own messages) don't generate error-level Sentry noise.
    sentryCaptureException(new Error('Unknown login error'), { level: 'error', extra: { originalError: err } });
  }
  actions.showError(loginErrorMessage(err, ssoProvider));
}

// ---------------------------------------------------------------------------
// State + action hooks
// ---------------------------------------------------------------------------

type LoginState = {
  step: LoginStep;
  email: string;
  password: string;
  twoFactorAuthCode: string;
  twoFactorAuthLoginToken: string;
  emailError: string | undefined;
  isSubmitting: boolean;
  hasSubmittedEmail: boolean;
  ssoConfiguration: SsoConfiguration | null;
  userExternalIdentityEmail: string | null;
  chooseUserEntries: ChooseUserEntryDataObject[];
  chooseUserJwtToken: string;
  googleAccountEmail: string;
  setStep: (step: LoginStep) => void;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setTwoFactorAuthCode: (value: string) => void;
  setTwoFactorAuthLoginToken: (value: string) => void;
  setEmailError: (value: string | undefined) => void;
  setIsSubmitting: (value: boolean) => void;
  setHasSubmittedEmail: (value: boolean) => void;
  setSsoConfiguration: (value: SsoConfiguration | null) => void;
  setUserExternalIdentityEmail: (value: string | null) => void;
  setChooseUserEntries: (value: ChooseUserEntryDataObject[]) => void;
  setChooseUserJwtToken: (value: string) => void;
  setGoogleAccountEmail: (value: string) => void;
};

type LoginCore = {
  loginMeta: () => LoginMeta;
  runLogin: (
    authenticate: () => Promise<unknown>,
    method?: LoginMethod,
    ssoProvider?: string,
    isAddingAccountOverride?: boolean,
  ) => Promise<boolean>;
};

function useLoginCore(state: LoginState, isAddingAccount: boolean, origin: string | undefined): LoginCore {
  const snackbars = useSnackbars();
  const {
    setStep,
    setIsSubmitting,
    setTwoFactorAuthLoginToken,
    setTwoFactorAuthCode,
    setChooseUserEntries,
    setChooseUserJwtToken,
    setGoogleAccountEmail,
  } = state;

  const loginMeta = useCallback(
    (): LoginMeta => ({ ga_client_id: getGaClientId(), add_session: isAddingAccount }),
    [isAddingAccount],
  );

  const handleLoginError = useCallback(
    (err: unknown, ssoProvider?: string) => {
      applyLoginError(
        err,
        {
          goToStep: setStep,
          goToChooser: (entries, jwtToken, googleAccountEmail) => {
            setChooseUserEntries(entries);
            setChooseUserJwtToken(jwtToken);
            setGoogleAccountEmail(googleAccountEmail);
            setStep('choose_user');
          },
          setTwoFactorAuthLoginToken,
          clearTwoFactorAuthCode: () => setTwoFactorAuthCode(''),
          showError: (message) => snackbars.create({ variant: 'critical', id: 'login.error', message }),
        },
        ssoProvider,
      );
    },
    [
      snackbars,
      setStep,
      setTwoFactorAuthLoginToken,
      setTwoFactorAuthCode,
      setChooseUserEntries,
      setChooseUserJwtToken,
      setGoogleAccountEmail,
    ],
  );

  const runLogin = useCallback(
    async (
      authenticate: () => Promise<unknown>,
      method?: LoginMethod,
      ssoProvider?: string,
      // The 2FA email-link flow carries the add-account intent in its JWT
      // (`add_session`), not the URL, so it overrides `isAddingAccount` here —
      // matching Ember, which set `addAccount` from the decoded token before
      // login so the "logged into new account" event still fired.
      isAddingAccountOverride?: boolean,
    ): Promise<boolean> => {
      setIsSubmitting(true);
      try {
        const response = await authenticate();
        applyAddSessionResponse(response);
        if (isAddingAccount || isAddingAccountOverride === true) {
          trackLoginEvent(LoginAnalyticsEvent.LoggedIntoNewAccount, {
            'Session Count': sessionsStore.get().length,
            'Login Method': method,
          });
        }
        // Only the email/password and Google flows record a "last used" method
        // (for the badge on the email step). SSO logins record nothing, matching
        // Ember's loginTask, so the SSO step doesn't pass a method.
        if (method != null) {
          setLastLoginMethod(method);
        }
        hardRedirect(resolvePostLoginTarget(origin));
        return true;
      } catch (error) {
        // `ssoProvider` lets the error normalizer build the provider-specific
        // SSO/one-click copy for a 401 (the API error carries no provider).
        handleLoginError(error, ssoProvider);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [origin, isAddingAccount, handleLoginError, setIsSubmitting],
  );

  return { loginMeta, runLogin };
}

type LoginActions = Pick<
  LoginFlowValue,
  | 'submitEmail'
  | 'submitPassword'
  | 'submitTwoFactor'
  | 'validateTwoFactorLink'
  | 'submitSso'
  | 'googleOneClick'
  | 'chooseUser'
  | 'resetToEmailStep'
>;

type BasicActions = Pick<
  LoginActions,
  'submitEmail' | 'submitPassword' | 'submitTwoFactor' | 'validateTwoFactorLink' | 'resetToEmailStep'
>;

function useBasicLoginActions(state: LoginState, isAddingAccount: boolean, core: LoginCore): BasicActions {
  const { email, password, twoFactorAuthCode, twoFactorAuthLoginToken } = state;
  const { setEmail, setStep, setPassword, setTwoFactorAuthCode, setTwoFactorAuthLoginToken } = state;
  const { setEmailError, setIsSubmitting, setHasSubmittedEmail } = state;
  const { setSsoConfiguration, setUserExternalIdentityEmail } = state;
  const { setChooseUserEntries, setChooseUserJwtToken, setGoogleAccountEmail } = state;
  const { loginMeta, runLogin } = core;

  // Clear any SSO/chooser state established by a previous email so a new lookup
  // (or "use a different email") never authenticates against a stale config.
  const clearSsoState = useCallback(() => {
    setSsoConfiguration(null);
    setUserExternalIdentityEmail(null);
    setChooseUserEntries([]);
    setChooseUserJwtToken('');
    setGoogleAccountEmail('');
  }, [
    setSsoConfiguration,
    setUserExternalIdentityEmail,
    setChooseUserEntries,
    setChooseUserJwtToken,
    setGoogleAccountEmail,
  ]);

  const submitEmail = useCallback(async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(undefined);
    // Persist the trimmed/normalized address so the password and 2FA steps
    // authenticate with the same value the SSO lookup used.
    setEmail(parsed.data);
    setIsSubmitting(true);
    try {
      const { ssoConfiguration: config, userExternalIdentityEmail: externalEmail } = await fetchSsoInfo({
        email: parsed.data,
        addAccount: isAddingAccount,
      });
      if (config == null) {
        // No SSO for this address — clear any SSO config left over from a
        // previously-entered email before falling through to password.
        clearSsoState();
        setStep('password');
        return;
      }
      setSsoConfiguration(config);
      setUserExternalIdentityEmail(externalEmail);
      setStep('sso');
    } catch {
      clearSsoState();
      setStep('password');
    } finally {
      setIsSubmitting(false);
      // The email step has now run SSO discovery this session; password/2FA
      // become reachable (a prefilled `?email=` alone does not qualify).
      setHasSubmittedEmail(true);
    }
  }, [
    email,
    isAddingAccount,
    clearSsoState,
    setEmail,
    setStep,
    setEmailError,
    setIsSubmitting,
    setHasSubmittedEmail,
    setSsoConfiguration,
    setUserExternalIdentityEmail,
  ]);

  const submitPassword = useCallback(async () => {
    await runLogin(
      () => session.authenticate(Authenticators.EmailPassword, { email, password, ...loginMeta() }),
      LoginMethod.Email,
    );
  }, [runLogin, email, password, loginMeta]);

  const submitTwoFactor = useCallback(async () => {
    await runLogin(
      () =>
        session.authenticate(Authenticators.TwoFactor, {
          '2fa_login_token': twoFactorAuthLoginToken,
          '2fa_code': twoFactorAuthCode,
          ...loginMeta(),
        }),
      LoginMethod.Email,
    );
  }, [runLogin, twoFactorAuthLoginToken, twoFactorAuthCode, loginMeta]);

  const validateTwoFactorLink = useCallback(
    (token: string) => {
      // The link token itself carries whether this is an add-account flow;
      // honor it over the URL's ?addAccount (Ember decodes the JWT here). Decode
      // up front so it both seeds the request meta and gates the add-account
      // analytics event in `runLogin`.
      const claims = decodeJwtPayload<{ add_session?: boolean }>(token);
      const isAddingAccountViaLink = claims?.add_session === true;
      return runLogin(
        async () => {
          const meta = loginMeta();
          if (claims != null) {
            meta.add_session = isAddingAccountViaLink;
          }
          try {
            await session.authenticate(Authenticators.TwoFactorLink, {
              token,
              cookie: getTwoFactorAuthLinkCookie(),
              ...meta,
            });
          } catch (error) {
            // An expired/invalid link token returns a 401. Ember surfaced a
            // specific message here rather than the generic SSO copy the error
            // normalizer would otherwise produce for a 401.
            const is401 =
              isUnknownLoginError(error) &&
              Array.isArray(error.errors) &&
              // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
              (error.errors as Array<{ status?: string }>).some((e) => e.status === '401');
            if (is401) {
              throw new LoginError(LoginErrorType.INVALID_EMAIL_PASSWORD, 'Invalid authentication token');
            }
            throw error;
          }
          removeTwoFactorAuthLinkCookie();
        },
        LoginMethod.Email,
        undefined,
        isAddingAccountViaLink,
      );
    },
    [runLogin, loginMeta],
  );

  const resetToEmailStep = useCallback(() => {
    // Mirror Ember's `useDifferentEmail`, which calls `setDefaults()` — that
    // blanks `email` too. Clearing it here both empties the email field for a
    // fresh entry and lets `resolveEffectiveStep` fall back to the email step
    // when the user navigates back to `/login/password` (Ember's password route
    // bounced to `login.index` on `!email`). Also reset `hasSubmittedEmail` so a
    // subsequent sub-step visit re-runs SSO discovery rather than trusting a
    // stale submission.
    setEmail('');
    setHasSubmittedEmail(false);
    setPassword('');
    setTwoFactorAuthCode('');
    setTwoFactorAuthLoginToken('');
    setEmailError(undefined);
    clearSsoState();
    // Clear any pending 2FA email-link cookie before returning to the email
    // step, otherwise the email-step cookie redirect bounces the user straight
    // back to `2fa_email_link` — "use a different email" must reach a fresh
    // email step from any step (password / SSO / chooser), not just the
    // email-link step. Cleared before `setStep('email')` so the redirect sees
    // the cookie already gone.
    removeTwoFactorAuthLinkCookie();
    trackLoginEvent(LoginAnalyticsEvent.SignInWithDifferentEmailClicked);
    setStep('email');
  }, [
    setEmail,
    setHasSubmittedEmail,
    setPassword,
    setTwoFactorAuthCode,
    setTwoFactorAuthLoginToken,
    setEmailError,
    clearSsoState,
    setStep,
  ]);

  return { submitEmail, submitPassword, submitTwoFactor, validateTwoFactorLink, resetToEmailStep };
}

type SsoActions = Pick<LoginActions, 'submitSso' | 'googleOneClick' | 'chooseUser'>;

function useSsoActions(state: LoginState, isAddingAccount: boolean, core: LoginCore): SsoActions {
  const { email, ssoConfiguration, userExternalIdentityEmail, chooseUserJwtToken } = state;
  const { setSsoConfiguration, setEmail } = state;
  const { loginMeta, runLogin } = core;

  const googleOneClick = useCallback(
    async (oneClickEmail = '') => {
      trackLoginEvent(LoginAnalyticsEvent.SignInWithGoogleClicked);
      await runLogin(
        async () => {
          const { authorizationCode, redirectUri } = await openGoogleOIDCPopup();
          return session.authenticate(Authenticators.GoogleOneClick, {
            code: authorizationCode,
            redirect_uri: redirectUri,
            ...(isEmptyString(oneClickEmail) ? {} : { email: oneClickEmail }),
            ...loginMeta(),
          });
        },
        LoginMethod.Google,
        'google_oidc',
      );
    },
    [runLogin, loginMeta],
  );

  const submitSso = useCallback(async () => {
    if (ssoConfiguration == null) {
      return;
    }
    // First-time Google SSO (no external identity yet) goes through One-Click.
    if (ssoConfiguration.identity_provider === 'google_oidc' && isEmptyString(userExternalIdentityEmail)) {
      await googleOneClick(email);
      return;
    }
    await runLogin(
      () =>
        authenticateWithSso(ssoConfiguration, email, {
          userExternalIdentityEmail: userExternalIdentityEmail ?? '',
          meta: loginMeta(),
        }),
      undefined,
      ssoConfiguration.identity_provider,
    );
  }, [ssoConfiguration, email, userExternalIdentityEmail, googleOneClick, runLogin, loginMeta]);

  const chooseUser = useCallback(
    async (entry: FormattedChooseUserEntry) => {
      if (entry.hasGoogleSSODomainMismatch) {
        return;
      }
      if (isOtherThanGoogleSso(entry.accountSSOProvider)) {
        // Run the whole exchange through `runLogin` so the form locks during the
        // SSO-info fetch and any failure (including SSO no longer being
        // configured) surfaces an error instead of failing silently.
        await runLogin(
          async () => {
            const { ssoConfiguration: config } = await fetchSsoInfo({
              email: entry.userEmail,
              addAccount: isAddingAccount,
            });
            if (config == null) {
              throw new Error('Single sign-on is no longer configured for this account.');
            }
            setSsoConfiguration(config);
            setEmail(entry.userEmail);
            return authenticateWithSso(config, entry.userEmail, { userExternalIdentityEmail: '', meta: loginMeta() });
          },
          undefined,
          entry.accountSSOProvider,
        );
        return;
      }
      await runLogin(
        () =>
          session.authenticate(Authenticators.GoogleOneClickChooseUserFinish, {
            user_id: entry.userID,
            choose_user_jwt_token: chooseUserJwtToken,
            ...loginMeta(),
          }),
        LoginMethod.Google,
        'google_oidc',
      );
    },
    [isAddingAccount, chooseUserJwtToken, runLogin, loginMeta, setSsoConfiguration, setEmail],
  );

  return { submitSso, googleOneClick, chooseUser };
}

type LoginFlowProviderProps = {
  children: ReactNode;
  /** Current step, derived from the URL by the route. */
  step: LoginStep;
  /** Navigate to a step (updates the URL). */
  onNavigateStep: (step: LoginStep) => void;
  initialEmail: string;
  isAddingAccount: boolean;
  origin: string | undefined;
};

function useLoginState(initialEmail: string): Omit<LoginState, 'step' | 'setStep'> {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [twoFactorAuthCode, setTwoFactorAuthCode] = useState('');
  const [twoFactorAuthLoginToken, setTwoFactorAuthLoginToken] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const [ssoConfiguration, setSsoConfiguration] = useState<SsoConfiguration | null>(null);
  const [userExternalIdentityEmail, setUserExternalIdentityEmail] = useState<string | null>(null);
  const [chooseUserEntries, setChooseUserEntries] = useState<ChooseUserEntryDataObject[]>([]);
  const [chooseUserJwtToken, setChooseUserJwtToken] = useState('');
  const [googleAccountEmail, setGoogleAccountEmail] = useState('');

  return {
    email,
    password,
    twoFactorAuthCode,
    twoFactorAuthLoginToken,
    emailError,
    isSubmitting,
    hasSubmittedEmail,
    ssoConfiguration,
    userExternalIdentityEmail,
    chooseUserEntries,
    chooseUserJwtToken,
    googleAccountEmail,
    setEmail,
    setPassword,
    setTwoFactorAuthCode,
    setTwoFactorAuthLoginToken,
    setEmailError,
    setIsSubmitting,
    setHasSubmittedEmail,
    setSsoConfiguration,
    setUserExternalIdentityEmail,
    setChooseUserEntries,
    setChooseUserJwtToken,
    setGoogleAccountEmail,
  };
}

export function LoginFlowProvider({
  children,
  step,
  onNavigateStep,
  initialEmail,
  isAddingAccount,
  origin,
}: LoginFlowProviderProps) {
  const state: LoginState = { ...useLoginState(initialEmail), step, setStep: onNavigateStep };
  const core = useLoginCore(state, isAddingAccount, origin);
  const basic = useBasicLoginActions(state, isAddingAccount, core);
  const sso = useSsoActions(state, isAddingAccount, core);

  // Clear the email error as the user edits so a stale "invalid email" doesn't
  // linger until the next submit (Ember re-validated on every keystroke).
  const setEmail = useCallback(
    (value: string) => {
      state.setEmail(value);
      state.setEmailError(undefined);
    },
    [state.setEmail, state.setEmailError],
  );

  // Key the email-step behaviors off the *effective* step, not the raw URL step,
  // so a direct/refresh visit to a sub-step that falls back to the email step
  // (e.g. `/login/password?email=…` before SSO discovery has run) still gets the
  // cookie redirect and `?email` auto-submit — mirroring Ember, where those
  // routes redirected to `login.index` and its `redirect()`/`setupController`
  // ran there.
  const effectiveStep = resolveEffectiveStep(step, {
    email: state.email,
    ssoConfiguration: state.ssoConfiguration,
    chooseUserEntries: state.chooseUserEntries,
    hasSubmittedEmail: state.hasSubmittedEmail,
    twoFactorAuthLoginToken: state.twoFactorAuthLoginToken,
  });

  // A pending 2FA email-link cookie jumps straight to that step. Mirrors the
  // Ember index route's `redirect()`, which re-checked the cookie on *every*
  // visit — so a browser-back to `/login` with the cookie still set re-sends the
  // user to `2fa_email_link` rather than stranding them on the email form.
  useEffect(() => {
    if (effectiveStep === 'email' && getTwoFactorAuthLinkCookie() != null) {
      onNavigateStep('2fa_email_link');
    }
  }, [effectiveStep, onNavigateStep]);

  // `?email=` (SSO team-invite links) pre-fills and auto-submits, mirroring the
  // Ember index `setupController`. Runs once: re-submitting on every email-step
  // visit would trap the user on auto-submit after "use a different email"
  // (the `?email` param is still in the URL).
  const hasAutoSubmitted = useRef(false);
  useEffect(() => {
    if (hasAutoSubmitted.current || effectiveStep !== 'email') {
      return;
    }
    // Consume the one-time opportunity as soon as the email step is first
    // shown, even if we don't submit here. Otherwise a pending 2FA cookie (or an
    // empty email) makes us bail without marking the ref, and a later return to
    // the email step — e.g. "use a different email" from the 2FA-link step,
    // which clears the cookie — would auto-submit the still-present `?email=`.
    hasAutoSubmitted.current = true;
    // Let the cookie redirect above win before auto-submitting an email.
    if (getTwoFactorAuthLinkCookie() != null || isEmptyString(initialEmail)) {
      return;
    }
    void basic.submitEmail();
  }, [effectiveStep, initialEmail, basic]);

  const value = useMemo<LoginFlowValue>(
    () => ({
      step: state.step,
      email: state.email,
      password: state.password,
      twoFactorAuthCode: state.twoFactorAuthCode,
      twoFactorAuthLoginToken: state.twoFactorAuthLoginToken,
      emailError: state.emailError,
      isSubmitting: state.isSubmitting,
      isAddingAccount,
      hasSubmittedEmail: state.hasSubmittedEmail,
      ssoConfiguration: state.ssoConfiguration,
      userExternalIdentityEmail: state.userExternalIdentityEmail,
      chooseUserEntries: state.chooseUserEntries,
      googleAccountEmail: state.googleAccountEmail,
      setEmail,
      setPassword: state.setPassword,
      setTwoFactorAuthCode: state.setTwoFactorAuthCode,
      ...basic,
      ...sso,
    }),
    [state, isAddingAccount, basic, sso, setEmail],
  );

  return <LoginFlowContext value={value}>{children}</LoginFlowContext>;
}
